import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { PlayerState } from '../shared/enums/player-states';
import { ConfigService } from '../shared/services/config.service';
import { AccountDevicesService } from '../shared/services/evergent/account-devices/account-devices.service';
import { GenerateDeviceActivationCodeResponseMessage } from '../shared/services/evergent/signup/model/activation-code/device-activation-code.response.model';
import { NavigationLibraryService } from '../shared/services/navigation/navigation.service';
import { LocalStorageService } from '../shared/services/storage/local-storage.service';
import { SimplePlayerService } from '../shared/shared-components/simple-player/simple-player.service';
import { KEYS } from '../shared/utils/constants/storage.constants';
import { LinkTvService } from './link-tv.service';

import { DataService } from '../data.service';
import { AuthService } from '../shared/services/auth/auth.service';
import { decodeToken, saveTokenExpiry } from '../shared/utils/LoginUtils';
import { Destroyable } from '../shared/services/destroyable';
import { RouteHistoryService } from '../shared/services/router-history.service';
import { MetaDataService } from '../shared/services/meta-data.service';
import { StorefrontService } from '../storefront/storefront.service';
import { AccountProfileService } from '../shared/services/evergent/account/account-profile.service';

interface Step {
  id: number;
  title: string;
  leftTitle?: string;
}

@Component({
  selector: 'app-link-tv',
  templateUrl: './link-tv.component.html',
  styleUrls: ['./link-tv.component.scss'],
})
export class LinkTvComponent extends Destroyable implements OnInit, OnDestroy {
  STEPS: Step[] = [
    {
      id: 1,
      title: 'open_aha_app_instruction',
    },
    {
      id: 2,
      title: 'navigate_to_my_aha',
    },
    {
      id: 3,
      title: 'select_link_tv_option',
    },
    {
      id: 4,
      title: 'link_tv_with_code',
      leftTitle: 'scan_qr_code',
    },
  ];

  isFocused = false;
  stateData: any;
  private activationCodeIntervalMS;
  loginCode: string = '------';
  private hasLogin = false;
  private activationCodeInterval: any;
  qrSize: number;
  private isApiCallInProgress = false;

  constructor(
    private readonly router: Router,
    private readonly configService: ConfigService,
    private readonly accountDevicesService: AccountDevicesService,
    private readonly linkTvService: LinkTvService,
    private readonly localStorage: LocalStorageService,
    private readonly navService: NavigationLibraryService,
    private readonly dataService: DataService,
    private readonly simplePlayerService: SimplePlayerService,
    private readonly routeHistoryService: RouteHistoryService,
    private readonly authService: AuthService,
    private readonly metaDataService: MetaDataService,
    private readonly storefrontService: StorefrontService,
    private readonly accountProfileService: AccountProfileService
  ) {
    super();
    this.activationCodeIntervalMS =
      parseInt(
        this.configService.applicationConfig.activationCodeInterval,
        10
      ) || 300;
  }

  ngOnInit(): void {
    this.stateData = window.history?.state;
    this.triggerActivation();
    this.navService.focusContainer('link-tv-container', true, 0);
    this.qrSize = (17.135 / 100) * window.innerWidth;
  }

  ngOnDestroy(): void {
    this.cleanup();
    if (this.activationCodeInterval) {
      clearInterval(this.activationCodeInterval);
    }
  }

  getLeftSteps(step: Step) {
    return step.leftTitle ? step.leftTitle : step.title;
  }

  private async triggerActivation() {
    if (this.hasLogin) {
      return;
    }
    try {
      const deviceRef = this.localStorage.getItem(KEYS.DEVICE_REF);
      /* if (deviceRef && deviceRef !== 'undefined') {
        await this.accountDevicesService.removeDevice(deviceRef).toPromise();
      } */
      this.getActivationCode();
      this.activationCodeInterval = setInterval(() => {
        if (this.hasLogin) {
          clearInterval(this.activationCodeInterval);
        } else {
          this.getActivationCode();
        }
      }, this.activationCodeIntervalMS);
    } catch (e) {
      console.warn('[LOGIN]', e);
    }
  }

  private getActivationCode() {
    if (this.isApiCallInProgress) {
      return;
    }

    this.isApiCallInProgress = true;
    this.linkTvService.getActivationCode((response) => {
      this.isApiCallInProgress = false;
      this.onSuccess(response);
    });
  }

  private onSuccess(response?: GenerateDeviceActivationCodeResponseMessage) {
    const data = response?.GenerateDeviceActivationCodeResponseMessage;
    if (data?.activationCode) {
      this.loginCode = data.activationCode;
    } else if (data?.accessToken) {
      if (this.hasLogin) {
        return;
      }
      this.hasLogin = true;
      this.configService.applicationConfig.channelPartnerID =
        data.channelPartnerID;
      this.linkTvService.setTokens(data);
      saveTokenExpiry(data.accessToken);
      this.authService.resetPlayback();
      this.accountProfileService
        .initializeAccountAndProfiles()
        .then(async () => {
          await this.authService.initPlatformService();
          // await this.authService.contentAuthorizer.registerDevice();
          this.simplePlayerService.updatePlayerState(PlayerState.STOP);
          this.storefrontService.initialiseServices();
          this.metaDataService.refresh();
          this.dataService.showLinkTvSuccessToast = true;
          this.dataService.detailResource = null;
          this.localStorage.setItem(KEYS.FRESH_LAUNCH, 'true');
          this.dataService.clearVerticalMenuLastEvent();
          this.dataService.isFromLinkTv = true;
          this.dataService.isFromDeeplink = false;
          this.router.navigate(['/']);
        })
        .catch((error) => {
          console.warn('Auth init error', error);
        });
    } else {
      console.error('[Error] Link TV Code Generation', data);
      // Removed recursive call to avoid stack overflow/infinite loop.
      // The interval polling handles retries gracefully.
    }
  }

  onBack() {
    if (!this.isApiCallInProgress) {
      this.routeHistoryService.goBack();
    }
  }

  trackByStepId(index: number, step: Step): number {
    return step.id;
  }
}
